/* eslint-disable react/prop-types */
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import React, { PureComponent } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import 'react-datepicker/dist/react-datepicker.css';
import FieldContainer from '../components/FieldContainer';
import { isFieldLocked } from '../components/utils';
import { formatDate } from '../utils/dateUtils';

function isTime(value) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
}

import { StringField } from './index';
import { locales } from '../utils/date';

const isAfterToday = (date) => {
  const currentDate = moment().format('YYYY-MM-DD');
  return moment(date).isSameOrAfter(currentDate);
};

function parseDateString(value, { showTimeSelect, showTimeSelectOnly, storeIsoDate }) {
  if (!value) return null;

  const today = moment().format('YYYY-MM-DD');
  if (storeIsoDate) {
    return moment(value, moment.ISO_8601).toDate();
  }
  if (showTimeSelectOnly) {
    return moment(`${today} ${value}`, 'YYYY-MM-DD HH:mm').toDate(); // hack: inject today with the selected time (HH:mm) because DatePicker need a date
  }
  if (showTimeSelect) {
    if (isTime(value)) {
      return moment(`${today} ${value}`, 'YYYY-MM-DD HH:mm').toDate(); // hack: inject today with the selected time (HH:mm) because DatePicker need a date
    }
    return moment(value, 'YYYY-MM-DD HH:mm').toDate();
  }
  return moment(value, 'YYYY-MM-DD').toDate();
}

function isDateWithTime(date) {
  return date.includes('T');
}

const computeIntervals = (includeDateIntervales, showTimeSelect) => {
  const hasIncludeIntervals = includeDateIntervales?.length > 0;
  if (hasIncludeIntervals) {
    const includeIntervals = includeDateIntervales
      .filter((range) => !!range.startDate && !!range.endDate)
      .map((range) => {
        if (!showTimeSelect && !range.startDate.includes('T')) {
          return {
            start: moment(range.startDate, 'YYYY-MM-DD').startOf('day').toDate(),
            end: moment(range.endDate, 'YYYY-MM-DD').endOf('day').toDate(),
          };
        }
        const { startDate, endDate } = range;
        if (showTimeSelect) {
          return {
            start: isDateWithTime(startDate) ? new Date(isDateWithTime) : moment(startDate, 'YYYY-MM-DD').startOf('day').toDate(),
            end: isDateWithTime(endDate) ? new Date(isDateWithTime) : moment(endDate, 'YYYY-MM-DD').endOf('day').toDate(),
          };
        }
        return { start: new Date(range.startDate), end: new Date(range.endDate) };
      });
    return { hasIncludeIntervals, includeIntervals };
  }

  return { hasIncludeIntervals };
};

function formatDateForSummary(value, { showTimeSelect, showTimeSelectOnly }) {
  if (!value || showTimeSelectOnly) return value;
  return formatDate(value, showTimeSelect ? 'LLLL' : 'LL');
}

export default class DateField extends PureComponent {
  render() {
    const {
      onChange,
      label,
      mode,
      name,
      value,
      showTimeSelect = false,
      showTimeSelectOnly = false,
      timeCaption,
      summary,
      includeDateIntervales = [],
      openToDate,
      timeIntervals = 10,
      storeIsoDate = false,
      lang,
      fromToday = false,
      dateFormat,
      required,
      calendarStartDay,
      disabled,
      locked,
    } = this.props;

    const isLocked = isFieldLocked(name, locked);
    const finalDateFormat = dateFormat ? dateFormat : showTimeSelect ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd';

    if (mode === 'summary') {
      if (summary?.hidden) {
        return null;
      }

      const formattedDate = formatDateForSummary(value, { dateFormat, showTimeSelect, showTimeSelectOnly });
      return <StringField label={summary?.label || label} value={formattedDate} mode="summary" />;
    }

    const selected = parseDateString(value, { showTimeSelect, showTimeSelectOnly, storeIsoDate });

    const { hasIncludeIntervals, includeIntervals } = computeIntervals(includeDateIntervales, showTimeSelect);
    const defaultOpenToDate = hasIncludeIntervals && orderBy(includeIntervals, 'start', 'asc')?.[0]?.start;

    function formatSaveDate(date) {
      if (!date) return null;
      const mDate = moment(date);
      if (!showTimeSelect) {
        return mDate.format('YYYY-MM-DD');
      }
      if (storeIsoDate) {
        return mDate.toISOString();
      }
      if (showTimeSelectOnly) {
        return mDate.format('HH:mm');
      }
      // Keep date using the user's timezone formatting
      return mDate.format('YYYY-MM-DD HH:mm');
    }

    return (
      <FieldContainer {...this.props}>
        <DatePicker
          className="af-string-field"
          // Will use default locale if not defined :)
          locale={lang && lang in locales ? lang : undefined}
          selected={selected}
          name={name}
          isClearable={false}
          onChange={(date) => onChange(name, formatSaveDate(date))}
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          timeFormat="HH:mm"
          timeIntervals={timeIntervals}
          dateFormat={showTimeSelectOnly ? 'HH:mm' : finalDateFormat}
          timeCaption={timeCaption || 'time'}
          includeDateIntervals={includeIntervals}
          openToDate={(openToDate && new Date(openToDate)) || defaultOpenToDate}
          filterDate={fromToday ? isAfterToday : undefined}
          required={required}
          calendarStartDay={calendarStartDay}
          disabled={disabled || isLocked}
        />
      </FieldContainer>
    );
  }
}
